import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, CreditCard, AlertCircle, CheckCircle2, ArrowLeft, X, ChevronLeft, ChevronRight, Mail, Ban, Gift } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';

// Define our subscription plan types
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  max_fotos_mes: number;
}

export const Paywall = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [activePlanIndex, setActivePlanIndex] = useState<number>(1); // Default to Negocio plan (middle)
  const sliderRef = useRef<HTMLDivElement>(null);
  const [promoMs, setPromoMs] = useState<number>(0);
  const [shortName, setShortName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [showPromoCountdown, setShowPromoCountdown] = useState<boolean>(false);
  
  // New states for subscription flow
  const [subscriptionStep, setSubscriptionStep] = useState<'initial' | 'email' | 'payment'>('initial');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [savingEmail, setSavingEmail] = useState<boolean>(false);
  const [cancellingSubscription, setCancellingSubscription] = useState<boolean>(false);
  const [cancelSuccess, setCancelSuccess] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showMpEmailTutorial, setShowMpEmailTutorial] = useState<boolean>(false);
  const [mpEmailTutorialStep, setMpEmailTutorialStep] = useState<1 | 2>(1);

  // Country/provider settings
  const [pais, setPais] = useState<string>('Argentina');
  const normalizedPais = (pais || '').toLowerCase();
  const isArgentina = normalizedPais === 'argentina';
  const isChile = normalizedPais === 'chile';
  const isColombia = normalizedPais === 'colombia';
  const isMexico = normalizedPais === 'méxico' || normalizedPais === 'mexico';
  const isPeru = normalizedPais === 'perú' || normalizedPais === 'peru';

  const currencyCode = isArgentina
    ? 'ARS'
    : isChile
    ? 'CLP'
    : isColombia
    ? 'COP'
    : isMexico
    ? 'MXN'
    : isPeru
    ? 'PEN'
    : 'USD';

  const providerName = isArgentina ? 'Mercado Pago' : 'Stripe';
  const providerLogoUrl = isArgentina
    ? 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//mercado-pago-logo.png'
    : 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/stripe-logotipo.png';

  const getPlanPrice = (planId: string, arsPrice: number) => {
    if (isArgentina) return arsPrice;

    if (isChile) {
      switch (planId) {
        case 'emprendimiento': return 14999;
        case 'negocio': return 49999;
        case 'enterprise': return 149999;
        default: return 0;
      }
    }

    if (isColombia) {
      switch (planId) {
        case 'emprendimiento': return 59999;   // $59.999 COP
        case 'negocio': return 199000;        // $199.000 COP
        case 'enterprise': return 550000;     // $550.000 COP
        default: return 0;
      }
    }

    if (isMexico) {
      switch (planId) {
        case 'emprendimiento': return 299;    // $299 MXN
        case 'negocio': return 999;          // $999 MXN
        case 'enterprise': return 2999;      // $2.999 MXN
        default: return 0;
      }
    }

    if (isPeru) {
      switch (planId) {
        case 'emprendimiento': return 50;     // 50 PEN
        case 'negocio': return 180;          // 180 PEN
        case 'enterprise': return 550;       // 550 PEN
        default: return 0;
      }
    }

    // Default: USD
    switch (planId) {
      case 'emprendimiento': return 25;
      case 'negocio': return 50;
      case 'enterprise': return 150;
      default: return 0;
    }
  };

  const formatPrice = (amount: number) => {
    const locale = isArgentina
      ? 'es-AR'
      : isChile
      ? 'es-CL'
      : isColombia
      ? 'es-CO'
      : isMexico
      ? 'es-MX'
      : isPeru
      ? 'es-PE'
      : 'en-US';

    const fmt = new Intl.NumberFormat(locale, { minimumFractionDigits: 0 });
    return `$${fmt.format(amount)} ${currencyCode}`;
  };

  // Plans configuration
  const plans: SubscriptionPlan[] = [
    {
      id: "emprendimiento",
      name: "Básico",
      price: 21999,
      description: "Para pequeños emprendimientos",
      features: [
        "Generación de fotos con IA",
        "Hasta 60 imágenes por mes",
        "Acceso desde computadora, tablet y celular",
        "~Generación de videos con IA",
        "~Función de Edición de imágenes con IA",
        "~Modo Pro (Imágenes ultra realistas)"
      ],
      max_fotos_mes: 60
    },
    {
      id: "negocio",
      name: "Pro",
      price: 69999,
      description: "Ideal para negocios en crecimiento",
      features: [
        "Generación de fotos con IA",
        "Hasta 250 imágenes por mes",
        "Generación de videos con IA",
        "Función de Edición de imágenes con IA",
        "Modo Pro (Imágenes ultra realistas)",
        "Acceso desde computadora, tablet y celular",
        "Soporte prioritario"
      ],
      max_fotos_mes: 250
    },
    {
      id: "enterprise",
      name: "Ultra",
      price: 209999,
      description: "Pensado para negocios con múltiples sucursales",
      features: [
        "Generación de fotos con IA",
        "Hasta 900 imágenes por mes",
        "Generación de videos con IA",
        "Videos pro (videos ultra realistas)",
        "Función de Edición de imágenes con IA",
        "Modo Pro (Imágenes ultra realistas)",
        "Acceso desde computadora, tablet y celular",
        "Soporte dedicado 24/7",
        "Mejor relación precio / imagen",
        "Asesoría estratégica para campañas"
      ],
      max_fotos_mes: 900
    }
  ];

  useEffect(() => {
    fetchUserProfile();
    
    // Scroll to the middle plan (Negocio) by default on mobile
    const timer = setTimeout(() => {
      scrollToActivePlan();
      
      // Ensure all cards have the same height
      equalizeCardHeights();
    }, 100);
    // Additional passes to catch late layout/async font changes
    const timer2 = setTimeout(() => {
      equalizeCardHeights();
    }, 400);
    const timer3 = setTimeout(() => {
      equalizeCardHeights();
    }, 800);
    
    // Handle resize events to maintain equal heights
    window.addEventListener('resize', handleResize);
    
    // Only apply specific styles to the slider, not the entire document
    if (sliderRef.current) {
      sliderRef.current.style.overflowX = 'auto';
      sliderRef.current.style.overflowY = 'visible';
    }
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Start a simple 57:00 countdown when page loads (non-persistent across navigations)
  useEffect(() => {
    setPromoMs(57 * 60 * 1000);
    const id = setInterval(() => {
      setPromoMs(prev => (prev <= 1000 ? 0 : prev - 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  
  // Re-equalize heights when active card changes (e.g., after scroll snap)
  useEffect(() => {
    equalizeCardHeights();
  }, [activePlanIndex]);
  
  // Combined resize handler
  const handleResize = () => {
    equalizeCardHeights();
    // Fix any scroll issues after resize
    if (window.scrollY > 0) {
      window.scrollTo(0, 0);
    }
  };
  
  // Function to ensure all cards have equal heights
  const equalizeCardHeights = () => {
    // Dynamically equalize heights (mobile and desktop)
    const cards = document.querySelectorAll('.plan-card');
    if (cards.length === 0) return;
    
    // Reset heights first
    cards.forEach(card => {
      (card as HTMLElement).style.height = 'auto';
      (card as HTMLElement).style.minHeight = '0';
    });
    if (sliderRef.current) {
      sliderRef.current.style.height = 'auto';
    }
    
    // Find the tallest card
    let maxHeight = 0;
    cards.forEach(card => {
      const height = card.getBoundingClientRect().height;
      maxHeight = Math.max(maxHeight, height);
    });
    
    const h = Math.ceil(maxHeight);
    // Apply the height to all cards
    cards.forEach(card => {
      (card as HTMLElement).style.height = `${h}px`;
      (card as HTMLElement).style.minHeight = `${h}px`;
    });
    // Also set the slider container height so h-full works consistently
    if (sliderRef.current) {
      sliderRef.current.style.height = `${h}px`;
    }
  };

  // Function to scroll to the active plan
  const scrollToActivePlan = () => {
    if (sliderRef.current && window.innerWidth < 768) {
      const containerWidth = sliderRef.current.offsetWidth;
      const cardWidth = containerWidth * 0.75; // 75% of container width for each card
      const scrollPosition = (cardWidth + 8) * activePlanIndex; // 8px for margins
      
      try {
        sliderRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      } catch (e) {
        // Fallback for browsers that don't support smooth scrolling
        sliderRef.current.scrollLeft = scrollPosition;
      }
    }
  };
  
  // Handle navigation between plans
  const handlePlanNavigation = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev' 
      ? Math.max(0, activePlanIndex - 1)
      : Math.min(plans.length - 1, activePlanIndex + 1);
      
    setActivePlanIndex(newIndex);
    
    if (sliderRef.current) {
      const containerWidth = sliderRef.current.offsetWidth;
      const cardWidth = containerWidth * 0.75; // 75% of container width
      const scrollPosition = (cardWidth + 8) * newIndex; // 8px for margins
      
      sliderRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };
  
  // Handle scroll events to update active plan index
  const handleScroll = () => {
    if (sliderRef.current) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const containerWidth = sliderRef.current.offsetWidth;
      const cardWidth = containerWidth * 0.75; // 75% of container width
      const newIndex = Math.round(scrollLeft / (cardWidth + 8)); // 8px for margins
      
      if (newIndex !== activePlanIndex && newIndex >= 0 && newIndex < plans.length) {
        setActivePlanIndex(newIndex);
        // Re-equalize after the snap settles
        setTimeout(() => equalizeCardHeights(), 50);
      }
    }
  };
  
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id_company, name_short')
        .eq('id', user.id)
        .single();
        
      if (profileError) throw profileError;
      
      if (!profileData.id_company) throw new Error('No hay una empresa asociada a este usuario');
      
      const { data: company, error: companyError } = await supabase
        .from('company_profiles')
        .select('plan, max_fotos_mes, pais, nombre, imagenes_totales_generadas')
        .eq('id_company', profileData.id_company)
        .single();
        
      if (companyError) throw companyError;
      
      setProfile({
        ...profileData,
        user_id: user.id
      });
      if ((profileData as any)?.name_short) setShortName((profileData as any).name_short as string);
      if ((company as any)?.nombre) setCompanyName((company as any).nombre as string);
      
      setCurrentPlan(company.plan);
      if ((company as any)?.pais) setPais((company as any).pais as string);

      const total = (company as any)?.imagenes_totales_generadas as number | undefined;
      setShowPromoCountdown(typeof total === 'number' ? total < 22 : false);
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      setError(err.message || 'Error al cargar la información del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscription = async (planId: string) => {
    try {
      setProcessing(true);
      setError(null);
      
      const selectedPlan = plans.find(plan => plan.id === planId);
      if (!selectedPlan) throw new Error('Plan no encontrado');
      
      // Reset subscription flow to initial state
      setSubscriptionStep('initial');
      setContactEmail('');
      setEmailError(null);
      
      // Show payment modal
      setSelectedPlanId(planId);
      setShowPaymentModal(true);
      
    } catch (err: any) {
      console.error('Error processing subscription:', err);
      setError(err.message || 'Error al procesar la suscripción');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleNextStep = () => {
    // If in initial step, move to email input step
    if (subscriptionStep === 'initial') {
      if (isArgentina) {
        setSubscriptionStep('email');
      } else {
        // For Stripe (non-Argentina), go straight to checkout
        handleContinueToCheckout();
      }
    }
  };

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  const handleSaveEmail = async () => {
    // Reset error state
    setEmailError(null);
    
    // Validate email
    if (!contactEmail.trim()) {
      setEmailError('El correo electrónico es obligatorio');
      return;
    }
    
    if (!validateEmail(contactEmail)) {
      setEmailError('Ingresa un correo electrónico válido');
      return;
    }
    
    try {
      setSavingEmail(true);
      
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no encontrado');
      
      // Get company ID for the current user
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
        
      if (profileError) throw profileError;
      if (!profileData.id_company) throw new Error('No hay una empresa asociada a este usuario');
      
      // Update company_profiles with the contact email
      const { error: updateError } = await supabase
        .from('company_profiles')
        .update({ email_contacto: contactEmail })
        .eq('id_company', profileData.id_company);
        
      if (updateError) throw updateError;
      
      // Move to payment step
      setSubscriptionStep('payment');
      
    } catch (err: any) {
      console.error('Error saving contact email:', err);
      setEmailError(err.message || 'Error al guardar el correo de contacto');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleContinueToCheckout = async () => {
    try {
      setProcessing(true);
      setShowPaymentModal(false);
      
      const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
      if (!selectedPlan) throw new Error('Plan no encontrado');
      
      setSuccess(`Procesando redirección a ${providerName}...`);
      
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no encontrado');
      
      // Send request to the provided API endpoint
      const endpoint = isArgentina
        ? 'https://ai-agency-dt7d.onrender.com/webhook/08741f54-6bda-4751-8054-14a59f8338ca'
        : 'https://ai-agency-dt7d.onrender.com/webhook/stripe-start';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          user_id: user.id,
          plan: selectedPlanId
        }),
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener el enlace de checkout');
      }
      
      const data = await response.json();
      
      // Redirect to the checkout URL returned by provider
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No se recibió una URL válida para el checkout');
      }
    } catch (err: any) {
      console.error('Error redirecting to checkout:', err);
      setError(err.message || `Error al redirigir al checkout de ${providerName}`);
      setProcessing(false);
    }
  };
  
  // Opens the cancel confirmation modal
  const handleOpenCancelModal = () => {
    setShowCancelModal(true);
  };

  // Close the cancel confirmation modal
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
  };
  
  // Handler for cancel subscription button after confirmation
  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      setError(null);
      setShowCancelModal(false); // Close the modal
      
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no encontrado');
      
      // Send request to the cancel subscription endpoint
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/ba02b716-44a0-44e7-a3b7-72c3f5ccfbcd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id
        }),
      });
      
      if (!response.ok) {
        throw new Error('Error al cancelar la suscripción');
      }
      
      const data = await response.json();
      
      if (data.response === 'success') {
        // Update UI to show success
        setCancelSuccess(true);
        setSuccess('Tu suscripción ha sido cancelada exitosamente.');
        
        // Refresh user profile to get updated plan status
        setTimeout(() => {
          fetchUserProfile();
        }, 2000);
      } else {
        throw new Error('Error al procesar la cancelación');
      }
    } catch (err: any) {
      console.error('Error cancelling subscription:', err);
      setError(err.message || 'Error al cancelar la suscripción');
    } finally {
      setCancellingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="px-4 py-2 md:p-8 w-full" style={{ maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
      {/* Mobile header with title and left back button */}
      <div className="sm:hidden mb-6 text-center relative flex justify-center items-center">
        <button 
          onClick={() => navigate(-1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 h-10 px-4 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 inline-flex items-center gap-1"
          aria-label="Volver"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        <h1 className="text-sm font-semibold text-gray-900">Planes de<br />Suscripción</h1>
      </div>
      <div className="mb-6 pl-1">
        <button 
          onClick={() => navigate(-1)} 
          className="hidden sm:inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver
        </button>
      </div>
      
      <div className="hidden sm:flex items-center gap-2 md:gap-3 mb-6 pl-1">
        <CreditCard className="w-6 h-6 md:w-8 md:h-8 text-gray-700" />
        <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Planes de Suscripción</h1>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start mx-1">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
          <span className="text-red-700">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-start mx-1">
          <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      <div className="mb-6 pl-1">
        {currentPlan === 'gratis' ? (
          <>
            <p className="text-gray-700 font-normal">
              <span className="text-blue-600">Subscribite</span> a un plan de Atlas para desbloquear todo el potencial de la IA
            </p>
            <p className="text-gray-700 font-normal mt-2">
              Tenés alguna duda?{' '}
              <a
                href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ''} de ${companyName || ''}. Me contactaba porque tenia una duda.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-600 hover:text-blue-700"
              >
                Contactanos
              </a>
            </p>
          </>
        ) : (
          <p className="text-gray-700 font-normal">
            <span className="text-blue-600">Mejora tu plan</span> para obtener mayores beneficios
          </p>
        )}
      </div>
      
      {/* Removed navigation arrows as requested */}
      
      {/* Plan Cards - Mobile Slider & Desktop Grid */}
      <div 
        ref={sliderRef}
        onScroll={handleScroll}
        className="md:grid md:grid-cols-3 md:gap-4 flex items-stretch overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          width: '100%',
          maxWidth: '100%',
          height: 'auto',
          touchAction: 'pan-x pan-y',
          position: 'relative',
          zIndex: 1
        }}
      >
        {plans.filter((plan) => {
          // Hide basic plan if user is subscribed to Pro or Ultra
          if (plan.id === 'emprendimiento' && (currentPlan === 'negocio' || currentPlan === 'enterprise')) {
            return false;
          }
          return true;
        }).map((plan) => (
          <div 
            key={plan.id} 
            className={`plan-card border rounded-md overflow-hidden flex-shrink-0 w-[75%] md:w-full min-w-[75%] md:min-w-0 mx-1 snap-center md:mx-0 bg-white ${currentPlan === plan.id ? 'border-blue-500' : 'border-gray-200'} h-full flex flex-col justify-between`}
          >
            <div className="p-4 md:p-6 flex flex-col h-full">
              <button
                onClick={() => handleSubscription(plan.id)}
                disabled={processing || currentPlan === plan.id}
                className={`md:hidden mb-2 inline-flex items-center justify-center h-10 px-4 rounded-full shadow-sm ${
                  currentPlan === plan.id 
                    ? 'bg-green-500 text-white cursor-default' 
                    : 'bg-blue-500 hover:bg-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : currentPlan === plan.id ? (
                  'Plan Actual'
                ) : (
                  'Subscribirse'
                )}
              </button>
              {currentPlan === plan.id && currentPlan !== 'gratis' && (
                <button
                  onClick={handleOpenCancelModal}
                  disabled={cancellingSubscription || cancelSuccess}
                  className={`md:hidden mb-3 flex items-center justify-center w-full h-8 px-4 rounded-full text-sm ${
                    cancelSuccess 
                      ? 'bg-green-100 text-green-700 cursor-default' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  {cancellingSubscription ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : cancelSuccess ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Cancelada
                    </>
                  ) : (
                    'Cancelar suscripción'
                  )}
                </button>
              )}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.name}</h3>
              <p className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
                {!isArgentina && (plan.id === 'negocio' || plan.id === 'enterprise') ? (
                  <>
                    <span className="flex items-center justify-start gap-2">
                      <span className="text-sm md:text-base font-normal text-gray-500 line-through">
                        {formatPrice(getPlanPrice(plan.id, plan.price))}
                      </span>
                      <span className="text-xs font-semibold text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E3070F' }}>🎄 50% OFF</span>
                    </span>
                    <span className="block">
                      {formatPrice(Math.floor(getPlanPrice(plan.id, plan.price) / 2))}
                      <span className="text-xs md:text-sm font-normal text-gray-600"> el primer mes</span>
                    </span>
                  </>
                ) : (
                  <>
                    {formatPrice(getPlanPrice(plan.id, plan.price))}
                    <span className="text-xs md:text-sm font-normal text-gray-600">/mes</span>
                  </>
                )}
              </p>
              <p className="text-gray-600 mb-4">{plan.description}</p>
              {plan.id === 'negocio' && currentPlan !== 'negocio' && currentPlan !== 'enterprise' && (
                <div className="mb-4 p-3 rounded-md border-2 text-sm relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #FFD700 100%)', borderColor: '#DAA520' }}>
                  <div className="absolute top-1 right-1 text-white opacity-30 text-2xl">✨</div>
                  <div className="absolute bottom-1 left-1 text-white opacity-30 text-xl">✨</div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold leading-tight text-white">🎉 Oferta Año Nuevo</p>
                  </div>
                  <p className="text-white">
                    {isArgentina
                      ? 'Si actualizás ahora al plan Pro, te regalamos +50 imágenes extra de bienvenida.'
                      : 'Si actualizás ahora al plan Pro, tenés un 50% de descuento en tu primer mes.'}
                  </p>
                  {showPromoCountdown && (
                    <p className="text-sm font-semibold mt-1 text-white">
                      {(() => {
                        const m = Math.floor(promoMs / 60000);
                        const s = Math.floor((promoMs % 60000) / 1000);
                        return `Termina en 2 hs ${m} min ${s.toString().padStart(2, '0')} seg`;
                      })()}
                    </p>
                  )}
                </div>
              )}
              {plan.id === 'enterprise' && currentPlan !== 'enterprise' && (
                <div className="mb-4 p-3 rounded-md border-2 text-sm relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #FFD700 100%)', borderColor: '#DAA520' }}>
                  <div className="absolute top-1 right-1 text-white opacity-30 text-2xl">✨</div>
                  <div className="absolute bottom-1 left-1 text-white opacity-30 text-xl">✨</div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold leading-tight text-white">🎉 Oferta Año Nuevo</p>
                  </div>
                  <p className="text-white">
                    {isArgentina
                      ? 'Si actualizás ahora al plan Ultra, te regalamos +200 imágenes extra de bienvenida.'
                      : 'Si actualizás ahora al plan Ultra, tenés un 50% de descuento en tu primer mes.'}
                  </p>
                  {showPromoCountdown && (
                    <p className="text-sm font-semibold mt-1 text-white">
                      {(() => {
                        const m = Math.floor(promoMs / 60000);
                        const s = Math.floor((promoMs % 60000) / 1000);
                        return `Termina en 2 hs ${m} min ${s.toString().padStart(2, '0')} seg`;
                      })()}
                    </p>
                  )}
                </div>
              )}
              <ul className="mb-6 flex-grow">
                {plan.features.map((feature, index) => {
                  const isDisabled = feature.startsWith('~');
                  const displayFeature = isDisabled ? feature.slice(1) : feature;
                  return (
                    <li key={index} className={`flex items-start md:items-center mb-2 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                      {isDisabled ? (
                        <X className="w-4 h-4 text-gray-400 mr-1 md:mr-2 mt-0.5 flex-shrink-0" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-blue-500 mr-1 md:mr-2 mt-0.5 flex-shrink-0" />
                      )}
                      <span className={`text-sm md:text-base ${!isDisabled && ((displayFeature.startsWith('Hasta ') && displayFeature.includes(' imágenes por mes')) || displayFeature.includes('Función de Edición de imágenes con IA') || displayFeature.includes('Modo Pro') || displayFeature.includes('Generación de videos con IA') || displayFeature.includes('Videos pro')) ? 'font-semibold' : ''}`}>{displayFeature}</span>
                    </li>
                  );
                })}
              </ul>
              
              <button
                onClick={() => handleSubscription(plan.id)}
                disabled={processing || currentPlan === plan.id}
                className={`hidden md:inline-flex items-center justify-center w-full h-10 px-4 rounded-full shadow-sm ${
                  currentPlan === plan.id 
                    ? 'bg-green-500 text-white cursor-default' 
                    : 'bg-blue-500 hover:bg-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : currentPlan === plan.id ? (
                  'Plan Actual'
                ) : (
                  'Subscribirse'
                )}
              </button>
              {currentPlan === plan.id && currentPlan !== 'gratis' && (
                <button
                  onClick={handleOpenCancelModal}
                  disabled={cancellingSubscription || cancelSuccess}
                  className={`mt-2 hidden md:flex items-center justify-center w-full h-9 px-4 rounded-full text-sm ${
                    cancelSuccess 
                      ? 'bg-green-100 text-green-700 cursor-default' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  {cancellingSubscription ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : cancelSuccess ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Cancelada
                    </>
                  ) : (
                    'Cancelar suscripción'
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Plan selection indicator dots - below cards */}
      <div className="flex justify-center space-x-2 my-4 md:hidden">
        {plans.map((_, index) => (
          <div 
            key={index}
            className={`w-2 h-2 rounded-full ${activePlanIndex === index ? 'bg-blue-500' : 'bg-gray-300'}`}
          />
        ))}
      </div>
      
      <div className="mt-6 md:mt-8 bg-gray-50 border border-gray-200 rounded-md p-4 md:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Información importante sobre suscripciones</h3>
        <ul className="space-y-2 text-gray-700">
          <li>• Las suscripciones se renuevan automáticamente cada mes.</li>
          <li>• Puedes cancelar tu suscripción en cualquier momento.</li>
          <li>• Al cambiar de plan, se aplicará el nuevo límite de fotos inmediatamente.</li>
          <li>• Los pagos son procesados de forma segura por {providerName}.</li>
        </ul>
      </div>
      
      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center z-50 p-4 overflow-y-auto pt-10" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-md shadow-lg max-w-md w-full p-4 md:p-6 relative my-4 md:my-0">
            <button 
              onClick={handleCloseCancelModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="mb-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <Ban className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmar cancelación</h3>
              <p className="text-gray-600 mb-4">
                ¿Estás seguro de que deseas cancelar tu suscripción? Al cancelar, perderás acceso a las funciones premium.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={handleCloseCancelModal}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Volver
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md font-normal shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center"
              >
                {cancellingSubscription ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : (
                  'Confirmar cancelación'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-md shadow-lg max-w-md w-full p-4 md:p-6 relative my-0">
            <button 
              onClick={() => {
                setShowPaymentModal(false);
                setSubscriptionStep('initial'); // Reset to initial step when closing modal
                setContactEmail('');
                setEmailError(null);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              Finaliza tu suscripción
            </h3>
            
            {selectedPlanId && (
              <div className="mb-6 border-b border-gray-200 pb-4">
                <p className="text-gray-700 mb-2">Plan seleccionado:</p>
                <p className="font-semibold text-gray-900">
                  {plans.find(p => p.id === selectedPlanId)?.name}
                </p>
                {(() => {
                  const selected = plans.find(p => p.id === selectedPlanId);
                  const regular = getPlanPrice(selectedPlanId, selected?.price ?? 0);
                  const hasFirstMonthDiscount = !isArgentina && (selectedPlanId === 'negocio' || selectedPlanId === 'enterprise');
                  if (hasFirstMonthDiscount) {
                    const discounted = Math.floor(regular / 2);
                    return (
                      <>
                        <p className="text-lg md:text-xl font-bold text-gray-900 mt-2">
                          {formatPrice(discounted)}
                          <span className="text-xs md:text-sm font-normal text-gray-600"> el primer mes</span>
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          Después {formatPrice(regular)} <span className="text-xs md:text-sm font-normal text-gray-600">/mes</span>
                        </p>
                      </>
                    );
                  }

                  return (
                    <p className="text-lg md:text-xl font-bold text-gray-900 mt-2">
                      {formatPrice(regular)}<span className="text-xs md:text-sm font-normal text-gray-600">/mes</span>
                    </p>
                  );
                })()}
              </div>
            )}
            
            <div className="mb-4">
              <div className="flex items-center justify-between p-3 rounded-md bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800 font-normal">Pago seguro por {providerName}</p>
                <img src={providerLogoUrl} alt={providerName} className="h-5 w-auto ml-3" />
              </div>
            </div>
            
            {/* Step 1: Initial Information */}
            {subscriptionStep === 'initial' && (
              <>
                <div className="mb-6">
                  <p className="text-gray-600 text-sm mb-4">
                    {isArgentina
                      ? 'Al hacer clic en "Continuar", podrás ingresar un correo de contacto para asociar con tu suscripción.'
                      : `Al hacer clic en "Continuar", serás redirigido a ${providerName} para completar tu suscripción de manera segura.`}
                  </p>
                  
                  <button
                    onClick={handleNextStep}
                    className="w-full py-2 md:py-3 px-3 md:px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-normal shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {isArgentina ? 'Continuar' : 'Continuar a pago seguro'}
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Email Input */}
            {subscriptionStep === 'email' && (
              <>
                <div className="mb-6">
                  <div className="mb-4">
                    <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">
                      {`Correo electrónico de ${providerName}`}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        type="email"
                        id="contact-email"
                        className="bg-white focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                        placeholder="ejemplo@correo.com"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                      />
                    </div>
                    {emailError && (
                      <p className="mt-2 text-sm text-red-600">{emailError}</p>
                    )}
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                    <p className="text-amber-800 text-sm font-semibold mb-1">Importante</p>
                    <p className="text-amber-700 text-sm">
                      {`Ingresa el correo asociado a la cuenta de ${providerName} con la que vas a pagar. Debe ser el mismo correo de tu cuenta de ${providerName}.`}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setMpEmailTutorialStep(1);
                        setShowPaymentModal(false);
                        setShowMpEmailTutorial(true);
                      }}
                      className="text-amber-800 text-sm underline mt-2 hover:text-amber-900 block text-left"
                    >
                      ¿Cómo saber el correo de mi cuenta de Mercado Pago?
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">
                    Este correo será asociado a tu cuenta de Atlas y utilizado para comunicaciones sobre tu suscripción.
                  </p>
                </div>
                
                <button
                  onClick={handleSaveEmail}
                  disabled={savingEmail}
                  className="w-full py-2 md:py-3 px-3 md:px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-normal shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {savingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    'Confirmar'
                  )}
                </button>
              </>
            )}

            {/* Step 3: Payment Information */}
            {subscriptionStep === 'payment' && (
              <>
                <div className="mb-6">
                  <p className="text-gray-600 text-sm mb-4">
                    Al hacer clic en "Continuar a pago seguro", serás redirigido al sitio de {providerName} para completar tu suscripción de manera segura.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md bg-gray-50">
                      <span className="text-gray-700">Pago seguro vía</span>
                      <div className="flex items-center">
                        <img 
                          src={providerLogoUrl}
                          alt={providerName}
                          style={{ height: '20px', width: 'auto', display: 'inline-block' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleContinueToCheckout}
                  disabled={processing}
                  className="w-full py-2 md:py-3 px-3 md:px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-normal shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    'Continuar a pago seguro'
                  )}
                </button>
              </>
            )}
            
            <div className="mt-4">
              <p className="text-xs text-gray-500 text-center">
                Al continuar aceptas los <a href={isArgentina ? "https://www.mercadopago.com.ar/ayuda/terminos-y-condiciones_299" : "https://stripe.com/legal"} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 underline">términos y condiciones</a> de {providerName}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mercado Pago Email Tutorial Modal */}
      {showMpEmailTutorial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] px-6 py-4">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-4 relative">
            <button
              onClick={() => {
                setShowMpEmailTutorial(false);
                setShowPaymentModal(true);
              }}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            
            {mpEmailTutorialStep === 1 ? (
              <>
                <div className="mb-4">
                  <img
                    src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/mp_como_ver_mi_mail.png"
                    alt="Paso 1: Pantalla de inicio de Mercado Pago"
                    className="w-full max-h-96 object-contain rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-700 mb-4">
                  Para conocer qué correo tienes asociado a tu cuenta de Mercado Pago, entra a la aplicación y en la pantalla de inicio haz click en la esquina superior izquierda.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setMpEmailTutorialStep(2)}
                    className="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Siguiente
                  </button>
                  <button
                    onClick={() => {
                      setShowMpEmailTutorial(false);
                      setShowPaymentModal(true);
                    }}
                    className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Atrás
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <img
                    src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/mp_como_ver_mi_mail2.png"
                    alt="Paso 2: Ver correo en Mercado Pago"
                    className="w-full max-h-96 object-contain rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-700 mb-4">
                  En la sección de abajo, debajo de tu nombre podrás ver el correo asociado a tu cuenta.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      setShowMpEmailTutorial(false);
                      setShowPaymentModal(true);
                    }}
                    className="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Entendido
                  </button>
                  <button
                    onClick={() => setMpEmailTutorialStep(1)}
                    className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Atrás
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Add custom CSS for hiding scrollbars while preserving functionality
const style = document.createElement('style');
style.textContent = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .hide-scrollbar {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .plan-slider {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
  }
  body, html {
    overflow-x: hidden;
    max-width: 100vw;
    position: relative;
  }
  .plan-card {
    flex: 0 0 auto;
    width: 70%;
    max-width: 300px;
    margin: 0 4px;
  }
  @media (min-width: 768px) {
    .plan-card {
      width: 100%;
      max-width: none;
      margin: 0;
    }
  }
`;
document.head.appendChild(style);

export default Paywall;
